import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Separator } from '@/components/ui/separator';
import { DialogFooter } from '@/components/ui/dialog';
import { Progress } from '@/components/ui/progress';
import { Edit2, Wifi, QrCode, Camera, Upload, Check, Info, Loader2 } from 'lucide-react';
import { DEVICE_TYPES, STREAM_TYPES } from './CCTVConstants';

export const CCTVDeviceForm = ({ device, onSubmit, onCancel, isLoading }) => {
  const [activeMethod, setActiveMethod] = useState(device?.connection_method || 'manual');
  const [formData, setFormData] = useState({
    name: device?.name || '', serial_number: device?.serial_number || '',
    device_type: device?.device_type || 'ip_camera', manufacturer: device?.manufacturer || '',
    model: device?.model || '', location: device?.location || '', group: device?.group || '',
    connection_method: device?.connection_method || 'manual',
    ip_address: device?.ip_address || '', port: device?.port || 554, ddns_host: device?.ddns_host || '',
    username: device?.username || '', password: '',
    stream_url: device?.stream_url || '', stream_type: device?.stream_type || 'rtsp',
    channel: device?.channel || 1, ptz_enabled: device?.ptz_enabled || false,
    audio_enabled: device?.audio_enabled || false, recording_enabled: device?.recording_enabled ?? true,
    motion_detection: device?.motion_detection || false, notes: device?.notes || ''
  });
  const [wifiStep, setWifiStep] = useState(0);
  const [wifiPassword, setWifiPassword] = useState('');

  return (
    <div className="space-y-6">
      <Tabs value={activeMethod} onValueChange={(v) => { setActiveMethod(v); setFormData({...formData, connection_method: v}); }}>
        <TabsList className="grid w-full grid-cols-2 sm:grid-cols-3">
          <TabsTrigger value="manual"><Edit2 className="h-4 w-4 mr-2" />Manual</TabsTrigger>
          <TabsTrigger value="wifi_config"><Wifi className="h-4 w-4 mr-2" />WiFi Config</TabsTrigger>
          <TabsTrigger value="qr_code"><QrCode className="h-4 w-4 mr-2" />QR Code</TabsTrigger>
        </TabsList>

        <TabsContent value="manual" className="space-y-4 mt-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Device Name *</Label>
              <Input value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})} placeholder="CAM01" data-testid="input-device-name" />
            </div>
            <div>
              <Label>Serial Number *</Label>
              <Input value={formData.serial_number} onChange={(e) => setFormData({...formData, serial_number: e.target.value})} placeholder="SN-123456789" data-testid="input-serial-number" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Device Type</Label>
              <Select value={formData.device_type} onValueChange={(v) => setFormData({...formData, device_type: v})}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{DEVICE_TYPES.map(t => <SelectItem key={t.id} value={t.id}>{t.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Location / Group</Label>
              <Input value={formData.location} onChange={(e) => setFormData({...formData, location: e.target.value})} placeholder="Main Gate" />
            </div>
          </div>
          <Separator />
          <h4 className="font-medium">Connection Settings</h4>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
            <div><Label>IP Address</Label><Input value={formData.ip_address} onChange={(e) => setFormData({...formData, ip_address: e.target.value})} placeholder="192.168.1.100" /></div>
            <div><Label>Port</Label><Input type="number" value={formData.port} onChange={(e) => setFormData({...formData, port: parseInt(e.target.value) || 554})} /></div>
            <div><Label>Channel</Label><Input type="number" value={formData.channel} onChange={(e) => setFormData({...formData, channel: parseInt(e.target.value) || 1})} min={1} /></div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div><Label>DDNS Host (Optional)</Label><Input value={formData.ddns_host} onChange={(e) => setFormData({...formData, ddns_host: e.target.value})} placeholder="mycam.ddns.net" /></div>
            <div>
              <Label>Stream Type</Label>
              <Select value={formData.stream_type} onValueChange={(v) => setFormData({...formData, stream_type: v})}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{STREAM_TYPES.map(t => <SelectItem key={t.id} value={t.id}>{t.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div><Label>Username</Label><Input value={formData.username} onChange={(e) => setFormData({...formData, username: e.target.value})} placeholder="admin" /></div>
            <div><Label>Password</Label><Input type="password" value={formData.password} onChange={(e) => setFormData({...formData, password: e.target.value})} placeholder={device ? '(unchanged)' : ''} /></div>
          </div>
          <div><Label>Stream URL (Optional)</Label><Input value={formData.stream_url} onChange={(e) => setFormData({...formData, stream_url: e.target.value})} placeholder="rtsp://..." /></div>
          <Separator />
          <h4 className="font-medium">Features</h4>
          <div className="grid grid-cols-2 gap-4">
            <div className="flex items-center gap-3"><Switch checked={formData.ptz_enabled} onCheckedChange={(c) => setFormData({...formData, ptz_enabled: c})} /><Label>PTZ Control</Label></div>
            <div className="flex items-center gap-3"><Switch checked={formData.audio_enabled} onCheckedChange={(c) => setFormData({...formData, audio_enabled: c})} /><Label>Audio</Label></div>
            <div className="flex items-center gap-3"><Switch checked={formData.recording_enabled} onCheckedChange={(c) => setFormData({...formData, recording_enabled: c})} /><Label>Recording</Label></div>
            <div className="flex items-center gap-3"><Switch checked={formData.motion_detection} onCheckedChange={(c) => setFormData({...formData, motion_detection: c})} /><Label>Motion Detection</Label></div>
          </div>
          <div><Label>Notes</Label><Textarea value={formData.notes} onChange={(e) => setFormData({...formData, notes: e.target.value})} placeholder="Additional notes..." rows={2} /></div>
        </TabsContent>

        <TabsContent value="wifi_config" className="mt-4">
          <Card><CardContent className="pt-6">
            {wifiStep === 0 && (
              <div className="text-center space-y-4">
                <div className="w-20 h-20 bg-blue-100 rounded-full mx-auto flex items-center justify-center"><Wifi className="h-10 w-10 text-blue-500" /></div>
                <h3 className="text-lg font-medium">WiFi Configuration</h3>
                <p className="text-muted-foreground">Make sure your phone is connected to the same WiFi network as the camera will use.</p>
                <div><Label>WiFi Password</Label><Input type="password" value={wifiPassword} onChange={(e) => setWifiPassword(e.target.value)} placeholder="Enter your WiFi password" className="max-w-xs mx-auto" /></div>
                <Button onClick={() => setWifiStep(1)}>Next</Button>
              </div>
            )}
            {wifiStep === 1 && (
              <div className="text-center space-y-4">
                <div className="w-20 h-20 bg-amber-100 rounded-full mx-auto flex items-center justify-center"><Info className="h-10 w-10 text-amber-500" /></div>
                <h3 className="text-lg font-medium">Position Your Devices</h3>
                <ul className="text-left max-w-md mx-auto space-y-2 text-muted-foreground">
                  <li className="flex items-center gap-2"><Check className="h-4 w-4 text-green-500" />Keep phone, router, and camera within 2 meters</li>
                  <li className="flex items-center gap-2"><Check className="h-4 w-4 text-green-500" />Power on the camera</li>
                  <li className="flex items-center gap-2"><Check className="h-4 w-4 text-green-500" />Long press SET button until indicator flashes quickly</li>
                </ul>
                <div className="flex gap-2 justify-center"><Button variant="outline" onClick={() => setWifiStep(0)}>Back</Button><Button onClick={() => setWifiStep(2)}>Continue</Button></div>
              </div>
            )}
            {wifiStep === 2 && (
              <div className="text-center space-y-4">
                <div className="w-20 h-20 bg-green-100 rounded-full mx-auto flex items-center justify-center"><Loader2 className="h-10 w-10 text-green-500 animate-spin" /></div>
                <h3 className="text-lg font-medium">Connecting...</h3>
                <p className="text-muted-foreground">Searching for nearby cameras...</p>
                <Progress value={66} className="max-w-xs mx-auto" />
                <div className="flex gap-2 justify-center"><Button variant="outline" onClick={() => setWifiStep(0)}>Cancel</Button></div>
              </div>
            )}
          </CardContent></Card>
        </TabsContent>

        <TabsContent value="qr_code" className="mt-4">
          <Card><CardContent className="pt-6">
            <div className="text-center space-y-4">
              <div className="w-20 h-20 bg-purple-100 rounded-full mx-auto flex items-center justify-center"><QrCode className="h-10 w-10 text-purple-500" /></div>
              <h3 className="text-lg font-medium">Scan QR Code</h3>
              <p className="text-muted-foreground">Scan the QR code on your camera or device label</p>
              <div className="flex flex-col gap-2 max-w-xs mx-auto">
                <Button><Camera className="h-4 w-4 mr-2" />Open Camera</Button>
                <Button variant="outline"><Upload className="h-4 w-4 mr-2" />Upload QR Image</Button>
              </div>
              <p className="text-xs text-muted-foreground">Or enter device serial manually above</p>
            </div>
          </CardContent></Card>
        </TabsContent>
      </Tabs>

      {activeMethod === 'manual' && (
        <DialogFooter>
          <Button type="button" variant="outline" onClick={onCancel}>Cancel</Button>
          <Button onClick={() => onSubmit(formData)} disabled={isLoading || !formData.name || !formData.serial_number} data-testid="submit-device-btn">
            {isLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
            {device ? 'Update Device' : 'Add Device'}
          </Button>
        </DialogFooter>
      )}
    </div>
  );
};
